import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight, Medal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  categoryStandingsService,
  type StandingsCategory,
} from '../../services/category-standings';

interface CategoryStandingsLinksProps {
  competitionSlug: string;
}

/**
 * The way from a stage's page to the standings of each class split over turns —
 * one table across the morning and the afternoon.
 *
 * Only a class with **two or more** turns, all of them **closed**: with one turn
 * the round's own standings already are the class's, and until every turn is
 * closed the order may still change in front of the people it is about (the
 * server refuses it then too — `turns_not_closed`). Nothing at all is drawn when
 * no class qualifies, and a failure to ask is silent: this is an extra on a page
 * that has its own job.
 */
export function CategoryStandingsLinks({ competitionSlug }: CategoryStandingsLinksProps) {
  const { t } = useTranslation();
  const [categories, setCategories] = useState<StandingsCategory[]>([]);

  useEffect(() => {
    let cancelled = false;

    categoryStandingsService
      .list(competitionSlug)
      .then((answer) => {
        if (!cancelled) setCategories(answer.categories);
      })
      .catch(() => undefined);

    return () => {
      cancelled = true;
    };
  }, [competitionSlug]);

  const published = categories.filter((category) => category.turns_count >= 2 && category.turns_closed);
  if (published.length === 0) return null;

  return (
    <Card data-testid="category-standings-links">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Medal className="h-5 w-5" aria-hidden />
          {t('competitionStandings.title')}
        </CardTitle>
        <p className="text-sm text-muted-foreground">{t('competitionStandings.hint')}</p>
      </CardHeader>
      <CardContent className="space-y-2">
        {published.map((category) => (
          <Link
            key={category.id}
            to={`/competitions/${competitionSlug}/standings/${category.id}`}
            className="flex items-center justify-between rounded-lg border p-3 transition-colors hover:bg-muted/50"
            data-testid="category-standings-link"
          >
            <span className="font-medium">{category.name}</span>
            <span className="flex items-center gap-2 text-sm text-muted-foreground">
              {t('competitionStandings.turns', { count: category.turns_count })}
              <ChevronRight className="h-4 w-4" aria-hidden />
            </span>
          </Link>
        ))}
      </CardContent>
    </Card>
  );
}

export default CategoryStandingsLinks;
